import React from 'react';
import { Card } from '../components/ui/Card';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';

export const SystemImportance = () => {
  const challenges = [
    {
      icon: FiIcons.FiLayers,
      title: 'Siloed Information',
      problem: 'Critical data sits in disconnected spreadsheets across 23+ ministries.',
      solution: 'Centralized Data Lake',
      impact: 'Single source of truth for all government performance data.'
    },
    {
      icon: FiIcons.FiClock,
      title: 'Reporting Latency',
      problem: 'Quarterly reports often arrive 3-6 months late, making data obsolete.',
      solution: 'Real-Time Monitoring',
      impact: 'Immediate visibility into project status and budget execution.'
    },
    {
      icon: FiIcons.FiAlertTriangle,
      title: 'Lack of Consequences',
      problem: 'Non-compliance often goes unnoticed until audits occur years later.',
      solution: 'Automated Escalations',
      impact: 'Instant triggers for breaches sent directly to OPM and Cabinet.'
    },
    {
      icon: FiIcons.FiFileMinus,
      title: 'Project Abandonment',
      problem: 'Infrastructure projects stall due to lack of early warning signs.',
      solution: 'Predictive Risk Models',
      impact: 'Early detection of inactivity prevents capital waste.'
    }
  ];

  const benefits = [
    {
      title: 'Fiscal Responsibility',
      desc: 'Ensures every Namibian Dollar allocated translates to tangible development outcomes.',
      stat: 'N$12.5B',
      statLabel: 'Portfolio Monitored',
      color: 'green'
    },
    {
      title: 'Legal Compliance',
      desc: 'Enforces statutory deadlines from the Public Procurement and State Finance Acts.',
      stat: '100%',
      statLabel: 'Audit Trail',
      color: 'blue'
    },
    {
      title: 'NDP6 Realization',
      desc: 'Directly links daily operational activities to high-level National Development Plan goals.',
      stat: '4',
      statLabel: 'Strategic Pillars',
      color: 'gold'
    },
    {
      title: 'Public Accountability',
      desc: 'Provides the transparency needed to hold public office bearers accountable.',
      stat: '24/7',
      statLabel: 'Oversight',
      color: 'navy'
    }
  ];

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-grn-navy to-blue-900 rounded-2xl p-8 md:p-12 overflow-hidden shadow-xl text-white">
        <div className="absolute top-0 right-0 p-8 opacity-10">
          <SafeIcon icon={FiIcons.FiShield} className="text-[200px]" />
        </div>
        <div className="relative z-10 max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 bg-grn-gold text-grn-navy text-xs font-bold rounded uppercase tracking-wider">
              System Mandate
            </span>
            <span className="px-3 py-1 bg-white/20 text-white text-xs font-bold rounded uppercase tracking-wider">
              Vision 2030 Enabler
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black mb-6 leading-tight">
            Why GovTrack is Critical for Namibia's Future
          </h1>
          <p className="text-lg md:text-xl text-blue-100 leading-relaxed mb-8">
            This platform is not just a reporting tool; it is the <strong>digital nervous system</strong> of the Namibian Government. It replaces reactive, paper-based bureaucracy with proactive, data-driven governance to ensure the successful implementation of <strong>NDP6</strong>.
          </p>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm border border-white/20">
              <SafeIcon icon={FiIcons.FiCheckCircle} className="text-grn-gold" />
              <span className="font-bold">Enforce Accountability</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm border border-white/20">
              <SafeIcon icon={FiIcons.FiTrendingUp} className="text-grn-gold" />
              <span className="font-bold">Accelerate Delivery</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm border border-white/20">
              <SafeIcon icon={FiIcons.FiEye} className="text-grn-gold" />
              <span className="font-bold">Ensure Transparency</span>
            </div>
          </div>
        </div>
      </div>

      {/* The Core Problem & Solution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">The Compliance Gap</h2>
          <p className="text-slate-600 mb-6">
            Historically, the gap between <strong>Policy Intent</strong> (what Cabinet decides) and <strong>Implementation Reality</strong> (what happens on the ground) has been widened by manual processes and lack of oversight. GovTrack closes this gap.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {challenges.map((item, idx) => (
              <Card key={idx} className="h-full border-l-4 border-l-grn-red hover:shadow-md transition-all">
                <div className="mb-3 p-2 bg-red-50 text-grn-red rounded-lg w-fit">
                  <SafeIcon icon={item.icon} className="text-xl" />
                </div>
                <h3 className="font-bold text-slate-800 mb-2">{item.title}</h3>
                <p className="text-xs text-slate-500 mb-2">{item.problem}</p>
                <div className="pt-2 border-t border-slate-100">
                  <p className="text-xs font-bold text-grn-navy flex items-center gap-1">
                    <SafeIcon icon={FiIcons.FiArrowRight} /> {item.solution}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="absolute inset-0 bg-grn-navy/5 rounded-2xl -rotate-1"></div>
          <Card className="h-full relative rotate-1 border-grn-navy/20">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Strategic Impact Pillars</h2>
            <div className="space-y-6">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-4">
                  <div className={clsx(
                    "flex flex-col items-center justify-center w-16 h-16 rounded-xl shrink-0 border",
                    benefit.color === 'green' ? "bg-green-50 border-green-200 text-green-700" :
                    benefit.color === 'blue' ? "bg-blue-50 border-blue-200 text-blue-700" :
                    benefit.color === 'gold' ? "bg-amber-50 border-amber-200 text-amber-700" :
                    "bg-slate-50 border-slate-200 text-grn-navy"
                  )}>
                    <span className="text-lg font-black">{benefit.stat}</span>
                    <span className="text-[9px] font-bold uppercase text-center leading-tight px-1">{benefit.statLabel}</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 text-lg">{benefit.title}</h3>
                    <p className="text-sm text-slate-600">{benefit.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-8 p-4 bg-slate-50 rounded-xl border border-slate-200">
              <h4 className="font-bold text-slate-800 mb-2 flex items-center gap-2">
                <SafeIcon icon={FiIcons.FiLock} className="text-grn-navy" />
                Legal Framework Alignment
              </h4>
              <p className="text-xs text-slate-500 italic">
                "The Prime Minister shall be responsible... for the leadership of Government business in Parliament, for the co-ordination of the work of the Cabinet and... to advise and assist the President in the execution of the functions of Government."
              </p>
              <p className="text-xs font-bold text-slate-700 mt-2 text-right">
                — Constitution of Namibia, Article 36
              </p>
            </div>
          </Card>
        </div>
      </div>

      {/* Critical Modules Breakdown */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Core Modules & Their Criticality</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="hover:border-grn-navy transition-colors group">
            <div className="h-40 bg-slate-100 rounded-lg mb-4 flex items-center justify-center group-hover:bg-blue-50 transition-colors">
              <SafeIcon icon={FiIcons.FiActivity} className="text-5xl text-slate-300 group-hover:text-grn-navy transition-colors" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Early Warning System</h3>
            <p className="text-sm text-slate-600">
              <strong>Why it's critical:</strong> Detects project delays and budget overruns <em>before</em> they become irreversible failures. Uses predictive logic to flag risks 30-60 days in advance.
            </p>
          </Card>

          <Card className="hover:border-grn-navy transition-colors group">
            <div className="h-40 bg-slate-100 rounded-lg mb-4 flex items-center justify-center group-hover:bg-blue-50 transition-colors">
              <SafeIcon icon={FiIcons.FiShield} className="text-5xl text-slate-300 group-hover:text-grn-navy transition-colors" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Automated Compliance</h3>
            <p className="text-sm text-slate-600">
              <strong>Why it's critical:</strong> Removes human discretion from compliance reporting. Deadlines for procurement and audits are tracked by the system, ensuring legal obligations are met.
            </p>
          </Card>

          <Card className="hover:border-grn-navy transition-colors group">
            <div className="h-40 bg-slate-100 rounded-lg mb-4 flex items-center justify-center group-hover:bg-blue-50 transition-colors">
              <SafeIcon icon={FiIcons.FiTarget} className="text-5xl text-slate-300 group-hover:text-grn-navy transition-colors" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">NDP6 Strategic Alignment</h3>
            <p className="text-sm text-slate-600">
              <strong>Why it's critical:</strong> Connects money spent to actual development impact. Ensures that every project contributes directly to specific NDP6 pillars and goals.
            </p>
          </Card>
        </div>
      </div>

      {/* Call to Action / Footer */}
      <div className="bg-slate-900 rounded-xl p-8 text-center text-white">
        <h2 className="text-2xl font-bold mb-4">A Zero-Tolerance Approach to Non-Performance</h2>
        <p className="text-slate-300 max-w-2xl mx-auto mb-6">
          GovTrack represents a paradigm shift in Namibian governance. By making performance visible, measurable, and tracked in real-time, we move from a culture of "reporting" to a culture of "results".
        </p>
        <div className="flex justify-center gap-4">
          <button className="px-6 py-3 bg-grn-gold text-grn-navy font-bold rounded-lg hover:bg-white transition-colors">
            View Live Dashboard
          </button>
          <button className="px-6 py-3 border border-slate-600 text-white font-medium rounded-lg hover:bg-white/10 transition-colors">
            Download Strategic Framework
          </button>
        </div>
      </div>
    </div>
  );
};